import {useCallback} from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector} from 'react-redux';
import {Unsubscribe} from 'redux';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch, getStore} from 'redux-stores/Store';

import {updateGlobalClientState} from './GlobalClientStateReduxActions';
import {GlobalClientStatesSelector} from './reduceGlobalClientStates';

export interface GlobalClientStateDefinition<TStateType> {
  globalClientStateUuid: string;
  defaultState: TStateType;
}

export type UseGlobalClientStateOutput<TStateType> = [TStateType, (newValue: TStateType) => void];

/**
 * @package
 */
export function useGlobalClientStateState<TStateType>(
  stateDefinition: GlobalClientStateDefinition<TStateType>,
  stateId: string
): UseGlobalClientStateOutput<TStateType> {
  const state = useSelector((state: BenchlingState) => {
    const globalClientStatesSelector = new GlobalClientStatesSelector(state);
    return globalClientStatesSelector.getGlobalClientState(stateDefinition, stateId);
  }, isEqual);

  const dispatch = useDispatch<BenchlingDispatch>();

  const setState = useCallback(
    (newState: TStateType) => dispatch(updateGlobalClientState(stateDefinition, stateId, newState)),
    [dispatch, stateDefinition, stateId]
  );

  return [state, setState];
}

export type UseGlobalClientStatesOutput<TStateType> = [
  {[stateId: string]: TStateType},
  (stateId: string, newValue: TStateType) => void,
];

/**
 * @package
 */
export function useGlobalClientStateStates<TStateType>(
  stateDefinition: GlobalClientStateDefinition<TStateType>,
  stateIds: Array<string>
): UseGlobalClientStatesOutput<TStateType> {
  const states = useSelector((state: BenchlingState) => {
    const globalClientStatesSelector = new GlobalClientStatesSelector(state);
    return globalClientStatesSelector.getGlobalClientStates(stateDefinition, stateIds);
  }, isEqual);

  const dispatch = useDispatch<BenchlingDispatch>();

  const setState = useCallback(
    (stateId: string, newValue: TStateType) => {
      assert(stateIds.includes(stateId), "Expected stateId to be defined in hook's input", {
        stateId,
        stateIds,
      });

      dispatch(updateGlobalClientState(stateDefinition, stateId, newValue));
    },
    [dispatch, stateDefinition, stateIds]
  );

  return [states, setState];
}

/**
 * Generate a hook that behaves like useState but is persisted through component remounts and globally sharable.
 *
 * The output hook will take in an id that is used to track the state in a key-value format. For example, in a
 * counter component, we might keep track of different counts because we render multiple versions of it, so
 * we might give each counter an id and track each count against an id.
 *
 * Example usage:
 *
 *    ```
 *    const counterState = makeUseGlobalClientState<number>(0)
 *
 *    function Counter(id: string): JSX.Element {
 *      const [count, setCount] = counterState.useState(id)
 *
 *      return <Button onClick={setCount(count + 1)} [...]>
 *        {count}
 *      </Button>
 *    }
 *    ```
 *
 * The state can be shared in multiple components by making the `counterState` public to other components and
 * calling the useState with the same ids.
 *
 * Mechanically, all the states are stored in redux under `BenchlingState.ui.globalClientStates` in the following shape:
 *    ```
 *    {
 *        [globalClientStateUuid]: {
 *          [stateId]: <state>
 *        }
 *    }
 *    ```
 *
 * @param defaultState The starting state before it's set for the first time. It's the same mechanic as
 *    useState's first arg.
 * @returns hooks and function used to interact with the state. See docstrings on output for more details.
 */
export default function makeUseGlobalClientState<TStateType>(defaultState: TStateType): {
  /**
   * Function that takes an individual state id and returns that state. This is useful for cases
   * where we want to read a shared state from outside a component (e.g. in a side effect function or in tests).
   * **WARNING**: the output of this function should never be rendered in components or used in hooks because
   * it doesn't create a Redux subscription, meaning your component or hook will not re-render when the state
   * changes. Use the output's hooks instead in those cases.
   */
  getState: (stateId: string) => TStateType;
  /**
   * A hook that takes a state id as input and returns a tuple containing the state
   * and a setter for that state. These behave the same way as useState's output.
   */
  useGlobalClientState: (stateId: string) => UseGlobalClientStateOutput<TStateType>;
  /**
   * A hook that takes multiple state ids and returns a tuple containing the map of
   * those state ids to those ids' corresponding states and a setter that takes a state id to update an individual
   * state. This hook is used to subscribe to and manage multiple states at the same time, which is usually useful
   * when you want to share lower level component's states with a higher level component.
   */
  useGlobalClientStates: (stateIds: Array<string>) => UseGlobalClientStatesOutput<TStateType>;
  /**
   * Function that takes an individual state id and updates that state. This is useful for cases
   * where we want to update a shared state from outside a component (e.g. as side effect to some function).
   */
  updateState: (stateId: string, newState: TStateType) => void;
  /**
   * Function that takes an individual state id and deletes that state. This is useful for cases
   * where we want to delete a shared state from outside a component (e.g. as side effect to some function
   * or when a component is unmounting).
   */
  deleteState: (stateId: string) => void;
  /**
   * Function that subscribes a listener to updates to the state. This is useful for cases
   * where we want to read and listen to a shared state from outside a component, where the other hooks
   * cannot be used, most likely in Backbone views.
   * Strongly prefer using the other hooks when possible.
   */
  subscribeListener: (stateId: string, listener: (state: TStateType) => void) => Unsubscribe;
} {
  const stateDefinition = {
    globalClientStateUuid: uuidv4(),
    defaultState,
  };

  function getState(stateId: string): TStateType {
    return (
      (getStore().getState().ui.globalClientStates[stateDefinition.globalClientStateUuid]?.[stateId] as
        | TStateType
        | undefined) ?? defaultState
    );
  }

  function subscribeListener(stateId: string, listener: (state: TStateType) => void): Unsubscribe {
    let oldState: TStateType = getState(stateId);
    return getStore().subscribe(() => {
      const newState = getState(stateId);
      if (!oldState || !newState || isEqual(oldState, newState)) {
        oldState = newState;
        return;
      }
      oldState = newState;
      listener(newState);
    });
  }

  return {
    getState,
    useGlobalClientState: (stateId) => useGlobalClientStateState<TStateType>(stateDefinition, stateId),
    useGlobalClientStates: (stateIds) => useGlobalClientStateStates<TStateType>(stateDefinition, stateIds),
    updateState: (stateId: string, newState: TStateType) => {
      getStore().dispatch(updateGlobalClientState(stateDefinition, stateId, newState));
    },
    deleteState: (stateId: string) => {
      getStore().dispatch(updateGlobalClientState(stateDefinition, stateId, defaultState));
    },
    subscribeListener,
  };
}
